import { Avatar, Box, Checkbox, Chip, IconButton, useTheme, Alert, AlertTitle, Button, Typography } from "@mui/material";
import { font } from "../../../../styles/font";
import { LightTooltip } from "../../../styled/CustomComponent";
import { ReplayOutlined, Person, ReportProblem, Refresh } from "@mui/icons-material";
import { AI_Entity, AI_Response_SentBy } from "../../../../constant/ApplicationVariables";
import { useEffect } from "react";
import { useApplicationContext } from "../../../../context/ApplicationContext";
import useErrorHandler from "../../../../api/errorHandler/useErrorHandler";
import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import dayjs from "dayjs";
import { AICustomIcon, AIDisplayIcon } from "../../../styled/CustomIcons";

export default function ResponseMessageContainer({ data, isLastConversation }) {
    console.log("ResponseMessageContainer data:", data);
    
    const {
        currentDataSavingFolder, setCurrentDataSavingFolder,
        currentAIEntityData,
        currentAIResponseSaveData, setCurrentAIResponseSaveData,
    } = useApplicationContext();
    const theme = useTheme();
    const errorHandler = useErrorHandler();
    const pathName = useLocation()?.pathname;
    const navigate = useNavigate();

    const [selectedResponseList, setSelectedResponseList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Check if this is an error response - moved up before hooks
    const isError = data?.isError === true || data?.error;
    const isUserMessage = data?.sentBy === AI_Response_SentBy.user;
    const isAIMessage = data?.sentBy === AI_Response_SentBy.assistant || !data?.sentBy;

    // All hooks must be called before any conditional returns
    useEffect(() => {
        console.log("ResponseMessageContainer received:", {
            data,
            sentBy: data?.sentBy,
            isUser: data?.sentBy === AI_Response_SentBy.user,
            isAI: data?.sentBy === AI_Response_SentBy.assistant,
            prompt: data?.prompt,
            response: data?.response,
            isError: isError,
            model: data?.model
        });
    }, [data, isError]);

    const getMessageContent = () => {
        if (isUserMessage) {
            return data?.prompt || data?.message || "";
        }
        if (data?.response || data?.message) {
            return data?.response || data?.message || "";
        }
        return "";
    };

    const getResponseItems = () => {
        if (isUserMessage || isError) {
            return [];
        }
        
        // Check if data has a list property directly
        if (data?.list && Array.isArray(data.list)) {
            console.log("Found list in data.list:", data.list.length);
            return data.list;
        }
        
        // Check if data.response has a list
        if (data?.response?.list && Array.isArray(data.response.list)) {
            console.log("Found list in data.response.list:", data.response.list.length);
            return data.response.list;
        }
        
        // Check if data is an array
        if (Array.isArray(data)) {
            console.log("Data is an array:", data.length);
            return data;
        }
        
        // Check if data.chatMessage is an array
        if (data?.chatMessage && Array.isArray(data.chatMessage)) {
            console.log("Found list in data.chatMessage:", data.chatMessage.length);
            return data.chatMessage;
        }
        
        // Check if data has a response that is an array
        if (data?.response && Array.isArray(data.response)) {
            console.log("data.response is an array:", data.response.length);
            return data.response;
        }
        
        // Check if data is a single object with id and name
        if (data && typeof data === 'object' && data.id && data.name) {
            console.log("Single item found:", data.name);
            return [data];
        }
        
        // Check if data.response is an object with id and name
        if (data?.response && typeof data.response === 'object' && 
            data.response.id && data.response.name) {
            console.log("Single item in response found:", data.response.name);
            return [data.response];
        }
        
        console.log("No list found in data:", data);
        return [];
    };

    const responseItems = getResponseItems();
    const messageContent = getMessageContent();
    const entityType = data?.entity || AI_Entity.userStory;

    const handleResponseComponent = (response, entity) => {
        if (!entity || entity === AI_Entity.userStory) {
            if (response?.acceptanceCriteria) return <UserStoryResponseComponent response={response} />;
            if (response?.useCaseSteps) return <UseCaseResponseComponent response={response} />;
            if (response?.testCaseSteps) return <TestCaseResponseComponent response={response} />;
        }
        switch (entity) {
            case AI_Entity.userStory: { return <UserStoryResponseComponent response={response} /> }
            case AI_Entity.useCase: { return <UseCaseResponseComponent response={response} /> }
            case AI_Entity.testCase: { return <TestCaseResponseComponent response={response} /> }
            case AI_Entity.folder: { return <FolderResponseComponent response={response} /> }
            default: { 
                return <UserStoryResponseComponent response={response} />;
            }
        }
    }

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const itemsWithId = responseItems.filter(item => item?.id);
            setSelectedResponseList(itemsWithId);
        } else {
            setSelectedResponseList([]);
        }
    }

    const handleToggleSelection = (e, item) => {
        if (e.target.checked && item?.id) {
            setSelectedResponseList([...selectedResponseList, item]);
        } else {
            setSelectedResponseList(selectedResponseList.filter((selected) => selected?.id !== item?.id));
        }
    }

    const isItemSelected = (item) => {
        return selectedResponseList.some(selected => selected?.id === item?.id);
    }

    // useEffect for selecting items - moved before conditional return
    useEffect(() => {
        if (!isUserMessage && responseItems.length > 0 && isLastConversation) {
            const itemsWithId = responseItems.filter(item => item?.id);
            setSelectedResponseList([...itemsWithId]);
        }
        if (responseItems.length > 0 && !isLastConversation) {
            setSelectedResponseList([]);
        }
    }, [responseItems, isLastConversation, isUserMessage]);

    // useEffect for saving response data - moved before conditional return
    useEffect(() => {
        if (!isUserMessage && selectedResponseList?.length > 0 && data?.responseId) {
            const ids = selectedResponseList
                .map((item) => item?.id)
                .filter(id => id !== null && id !== undefined);
            
            if (ids.length > 0) {
                setCurrentAIResponseSaveData({
                    "responseId": data?.responseId,
                    "conversationId": data?.conversationId,
                    "ids": ids,
                    "folderPath": "",
                    "saveDuplicate": false,
                });
            }
        } else if (isUserMessage) {
            setCurrentAIResponseSaveData(null);
        }
    }, [selectedResponseList, setCurrentAIResponseSaveData, data, isUserMessage]);

    console.log("ResponseMessageContainer debug:", {
        isUserMessage,
        isAIMessage,
        messageContent,
        responseItemsLength: responseItems.length,
        dataStructure: data,
        isError: isError,
        model: data?.model
    });

    // If error, render error component - now after all hooks
    if (isError) {
        return <ErrorMessageComponent data={data} theme={theme} />;
    }

    // Helper function to render model chip
    const renderModelChip = () => {
        if (!data?.model) return null;
        return (
            <Chip 
                label={data.model}
                size="small"
                sx={{
                    height: '20px',
                    fontSize: '0.6rem',
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? theme.palette.grey[800] 
                        : theme.palette.grey[200],
                    color: theme.palette.text.secondary,
                    '& .MuiChip-label': {
                        px: 1,
                    },
                    '@media (max-width: 600px)': {
                        height: '16px',
                        fontSize: '0.5rem',
                    }
                }}
            />
        );
    };

    const renderAIIcon = (size = 'small') => {
        return (
            <AIDisplayIcon 
                size={size}
                animated={true}
                sx={{ 
                    width: '1.4rem', 
                    height: '1.4rem',
                    '@media (max-width: 600px)': {
                        width: '1.2rem',
                        height: '1.2rem',
                    }
                }}
            />
        );
    };

    const renderUserMessage = () => {
        return (
            <Box className={`flex flex-col w-full items-end`}>
                <Box 
                    className={`flex flex-col px-4 py-3 w-fit max-w-[800px] gap-2`} 
                    border={`1px solid ${theme.palette.divider}`} 
                    borderRadius={theme.shape.borderRadius} 
                    bgcolor={theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light}
                    sx={{ 
                        borderColor: theme.palette.primary.main,
                        marginRight: '16px',
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.primary.dark 
                            : theme.palette.primary.light,
                        '@media (max-width: 600px)': {
                            marginRight: '8px',
                            padding: '8px 12px',
                            maxWidth: '92%',
                            borderRadius: '8px',
                        },
                        '@media (max-width: 400px)': {
                            marginRight: '4px',
                            padding: '6px 10px',
                            maxWidth: '95%',
                        }
                    }}
                >
                    <Box className="flex items-center gap-2">
                        <Avatar sx={{ 
                            width: "1.4rem", 
                            height: "1.4rem", 
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '@media (max-width: 600px)': {
                                width: '1.2rem',
                                height: '1.2rem',
                            }
                        }}>
                            <Person fontSize="small" />
                        </Avatar>
                        <Typography fontSize={font.size.fs_5} fontWeight="bold" 
                            color={theme.palette.mode === 'dark' 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.primary.dark}
                            sx={{
                                '@media (max-width: 600px)': {
                                    fontSize: '0.875rem',
                                }
                            }}>
                            You
                        </Typography>
                    </Box>

                    <Box className="flex flex-col gap-1">
                        <Typography fontSize={font.size.fs_5} 
                            color={theme.palette.mode === 'dark' 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.primary.dark} 
                            sx={{ 
                                wordBreak: 'break-word',
                                '@media (max-width: 600px)': {
                                    fontSize: '0.875rem',
                                }
                            }}>
                            {messageContent}
                        </Typography>
                    </Box>

                    <Box className="flex justify-end">
                        <Typography 
                            fontSize={font.size.fs_6} 
                            color={theme.palette.mode === 'dark' 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.primary.dark}
                            className="opacity-80"
                            sx={{
                                '@media (max-width: 600px)': {
                                    fontSize: '0.625rem',
                                }
                            }}
                        >
                            {dayjs(data?.createdAt || new Date()).format("DD MMM YYYY, hh:mm A")}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderAIResponse = () => {
        if (responseItems.length > 0) {
            return (
                <Box className={`flex flex-col w-full items-start`}>
                    <Box 
                        className={`flex flex-col px-4 py-3 w-fit max-w-[800px] gap-2`} 
                        border={`1px solid ${theme.palette.divider}`} 
                        borderRadius={theme.shape.borderRadius} 
                        bgcolor={theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50]}
                        sx={{ 
                            marginLeft: '16px',
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? theme.palette.background.paper 
                                : theme.palette.grey[50],
                            '@media (max-width: 600px)': {
                                marginLeft: '8px',
                                padding: '8px 10px',
                                maxWidth: '95%',
                                borderRadius: '8px',
                            },
                            '@media (max-width: 400px)': {
                                marginLeft: '4px',
                                padding: '6px 8px',
                                maxWidth: '97%',
                            }
                        }}
                    >
                        <Box className="flex items-center gap-2 flex-wrap">
                            {renderAIIcon()}
                            <Typography fontSize={font.size.fs_5} fontWeight="bold" 
                                color={theme.palette.mode === 'dark' 
                                    ? theme.palette.secondary.light 
                                    : theme.palette.info.dark}
                                sx={{
                                    '@media (max-width: 600px)': {
                                        fontSize: '0.875rem',
                                    }
                                }}>
                                AI Assistant
                            </Typography>
                            {renderModelChip()}
                        </Box>

                        <Box className={`flex flex-col w-full gap-0 px-0 py-0 rounded-lg relative`}>
                            <Box
                                className={`flex items-center flex-wrap w-full gap-2 py-2 px-3 overflow-hidden border-b sticky -top-0 z-10`}
                                bgcolor={theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]}
                                sx={{ 
                                    borderRadius: `${theme.shape.borderRadius} ${theme.shape.borderRadius} 0 0`,
                                    '@media (max-width: 600px)': {
                                        gap: '4px',
                                        py: '6px',
                                        px: '8px',
                                    },
                                    '@media (max-width: 400px)': {
                                        gap: '2px',
                                        py: '4px',
                                        px: '6px',
                                        flexWrap: 'nowrap',
                                    }
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={currentAIResponseSaveData && currentAIResponseSaveData?.responseId !== data?.responseId ? false : selectedResponseList.length === responseItems.length}
                                    indeterminate={selectedResponseList.length > 0 && selectedResponseList.length < responseItems.length}
                                    onChange={handleSelectAll}
                                    sx={{
                                        '@media (max-width: 600px)': {
                                            padding: '2px',
                                        },
                                        '@media (max-width: 400px)': {
                                            padding: '1px',
                                        }
                                    }}
                                />

                                <Avatar sx={{ 
                                    width: "1.4rem", 
                                    height: "1.4rem", 
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                                    '@media (max-width: 600px)': {
                                        width: '1.2rem',
                                        height: '1.2rem',
                                    },
                                    '@media (max-width: 400px)': {
                                        width: '1rem',
                                        height: '1rem',
                                    }
                                }}>
                                    <Typography fontSize={font.size.fs_5} 
                                        color={theme.palette.mode === 'dark' 
                                            ? theme.palette.grey[300] 
                                            : theme.palette.grey[700]}
                                        sx={{
                                            '@media (max-width: 600px)': {
                                                fontSize: '0.75rem',
                                            },
                                            '@media (max-width: 400px)': {
                                                fontSize: '0.625rem',
                                            }
                                        }}>
                                        {selectedResponseList.length}
                                    </Typography>
                                </Avatar>

                                <Box className="flex-1 flex items-center justify-end gap-2">
                                    {data?.saved && (
                                        <Chip 
                                            variant="outlined" 
                                            color="success" 
                                            label="Saved" 
                                            size="small"
                                            sx={{ 
                                                ml: "auto",
                                                '@media (max-width: 600px)': {
                                                    fontSize: '0.625rem',
                                                    height: '20px',
                                                },
                                                '@media (max-width: 400px)': {
                                                    fontSize: '0.5rem',
                                                    height: '16px',
                                                    '& .MuiChip-label': {
                                                        padding: '0 4px',
                                                    }
                                                }
                                            }} 
                                        />
                                    )}

                                    <LightTooltip title="Regenerate">
                                        <IconButton 
                                            disabled 
                                            color="primary" 
                                            sx={{ 
                                                ml: "auto",
                                                '@media (max-width: 600px)': {
                                                    padding: '4px',
                                                },
                                                '@media (max-width: 400px)': {
                                                    padding: '2px',
                                                }
                                            }}
                                            size="small"
                                        >
                                            <ReplayOutlined fontSize="small" />
                                        </IconButton>
                                    </LightTooltip>
                                </Box>
                            </Box>

                            <Box className={`flex flex-col w-full gap-2 p-3`}
                                sx={{
                                    '@media (max-width: 600px)': {
                                        padding: '8px',
                                        gap: '8px',
                                    },
                                    '@media (max-width: 400px)': {
                                        padding: '4px',
                                        gap: '4px',
                                    }
                                }}>
                                {responseItems.map((responseItem, index) => (
                                    <Box 
                                        key={responseItem.id || index} 
                                        className={`flex w-full gap-0 relative`} 
                                        borderRadius={theme.shape.borderRadius} 
                                        border={`1px solid ${theme.palette.divider}`}
                                        bgcolor={theme.palette.mode === 'dark' 
                                            ? theme.palette.grey[900] 
                                            : theme.palette.background.paper}
                                        sx={{
                                            '@media (max-width: 600px)': {
                                                flexDirection: 'column',
                                                borderRadius: '6px',
                                            },
                                            '@media (max-width: 400px)': {
                                                borderRadius: '4px',
                                            }
                                        }}
                                    >
                                        <Box className="flex h-fit">
                                            <Checkbox 
                                                size="small"
                                                disabled={currentAIResponseSaveData && currentAIResponseSaveData?.responseId !== data?.responseId} 
                                                checked={currentAIResponseSaveData && currentAIResponseSaveData?.responseId !== data?.responseId ? false : isItemSelected(responseItem)}
                                                onChange={(e) => handleToggleSelection(e, responseItem)}
                                                sx={{
                                                    '@media (max-width: 600px)': {
                                                        padding: '4px',
                                                    },
                                                    '@media (max-width: 400px)': {
                                                        padding: '2px',
                                                    }
                                                }} 
                                            />
                                        </Box>

                                        <Box className="flex-1 flex flex-col gap-1 p-2"
                                            sx={{
                                                '@media (max-width: 600px)': {
                                                    padding: '8px',
                                                },
                                                '@media (max-width: 400px)': {
                                                    padding: '4px',
                                                }
                                            }}>
                                            {handleResponseComponent(responseItem, entityType)}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box className="flex justify-end items-center gap-2">
                            <Typography 
                                fontSize={font.size.fs_6} 
                                color={theme.palette.text.secondary}
                                className="opacity-80"
                                sx={{
                                    '@media (max-width: 600px)': {
                                        fontSize: '0.625rem',
                                    }
                                }}
                            >
                                {dayjs(data?.createdAt || new Date()).format("DD MMM YYYY, hh:mm A")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            );
        }
        
        if (messageContent) {
            return (
                <Box className={`flex flex-col w-full items-start`}>
                    <Box 
                        className={`flex flex-col px-4 py-3 w-fit max-w-[800px] gap-2`} 
                        border={`1px solid ${theme.palette.divider}`} 
                        borderRadius={theme.shape.borderRadius} 
                        bgcolor={theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50]}
                        sx={{ 
                            marginLeft: '16px',
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? theme.palette.background.paper 
                                : theme.palette.grey[50],
                            '@media (max-width: 600px)': {
                                marginLeft: '8px',
                                padding: '8px 12px',
                                maxWidth: '92%',
                                borderRadius: '8px',
                            },
                            '@media (max-width: 400px)': {
                                marginLeft: '4px',
                                padding: '6px 10px',
                                maxWidth: '95%',
                            }
                        }}
                    >
                        <Box className="flex items-center gap-2 flex-wrap">
                            {renderAIIcon()}
                            <Typography fontSize={font.size.fs_5} fontWeight="bold" 
                                color={theme.palette.mode === 'dark' 
                                    ? theme.palette.secondary.light 
                                    : theme.palette.info.dark}
                                sx={{
                                    '@media (max-width: 600px)': {
                                        fontSize: '0.875rem',
                                    }
                                }}>
                                AI Assistant
                            </Typography>
                            {renderModelChip()}
                        </Box>

                        <Box className="flex flex-col gap-1">
                            <Typography fontSize={font.size.fs_5} 
                                color={theme.palette.text.primary} 
                                sx={{ 
                                    wordBreak: 'break-word',
                                    '@media (max-width: 600px)': {
                                        fontSize: '0.875rem',
                                    }
                                }}>
                                {messageContent}
                            </Typography>
                        </Box>

                        <Box className="flex justify-end items-center gap-2">
                            <Typography 
                                fontSize={font.size.fs_6} 
                                color={theme.palette.text.secondary}
                                className="opacity-80"
                                sx={{
                                    '@media (max-width: 600px)': {
                                        fontSize: '0.625rem',
                                    }
                                }}
                            >
                                {dayjs(data?.createdAt || new Date()).format("DD MMM YYYY, hh:mm A")}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            );
        }
        
        return (
            <Box className={`flex flex-col w-full items-start`}>
                <Box 
                    className={`flex flex-col px-4 py-3 w-fit max-w-[800px] gap-2`} 
                    border={`1px solid ${theme.palette.divider}`} 
                    borderRadius={theme.shape.borderRadius} 
                    bgcolor={theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50]}
                    sx={{ 
                        marginLeft: '16px',
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? theme.palette.background.paper 
                            : theme.palette.grey[50],
                        '@media (max-width: 600px)': {
                            marginLeft: '8px',
                            padding: '8px 12px',
                            maxWidth: '92%',
                            borderRadius: '8px',
                        }
                    }}
                >
                    <Box className="flex items-center gap-2 flex-wrap">
                        {renderAIIcon()}
                        <Typography fontSize={font.size.fs_5} fontWeight="bold" 
                            color={theme.palette.mode === 'dark' 
                                ? theme.palette.secondary.light 
                                : theme.palette.info.dark}
                            sx={{
                                '@media (max-width: 600px)': {
                                    fontSize: '0.875rem',
                                }
                            }}>
                            AI Assistant
                        </Typography>
                        {renderModelChip()}
                    </Box>

                    <Box className="flex items-center">
                        <Typography fontSize={font.size.fs_5} 
                            color={theme.palette.text.secondary} 
                            fontStyle="italic" 
                            className="opacity-60"
                            sx={{
                                '@media (max-width: 600px)': {
                                    fontSize: '0.875rem',
                                }
                            }}>
                            No response content available
                        </Typography>
                    </Box>

                    <Box className="flex justify-end items-center gap-2">
                        <Typography 
                            fontSize={font.size.fs_6} 
                            color={theme.palette.text.secondary}
                            className="opacity-80"
                            sx={{
                                '@media (max-width: 600px)': {
                                    fontSize: '0.625rem',
                                }
                            }}
                        >
                            {dayjs(data?.createdAt || new Date()).format("DD MMM YYYY, hh:mm A")}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    };

    return isUserMessage ? renderUserMessage() : renderAIResponse();
}

// Error Message Component
function ErrorMessageComponent({ data, theme }) {
    const error = data?.error || data?.message || "Something went wrong";
    const isCredentialError = error?.toLowerCase()?.includes('credential') || 
                             error?.toLowerCase()?.includes('api key') ||
                             error?.toLowerCase()?.includes('configure') ||
                             error?.toLowerCase()?.includes('provider rejected');

    return (
        <Box className={`flex flex-col w-full items-start animate-fadeIn`}>
            <Box 
                className={`flex flex-col px-4 py-3 w-fit max-w-[800px] gap-2`} 
                border={`1px solid ${theme.palette.error.main}`}
                borderRadius={theme.shape.borderRadius} 
                bgcolor={theme.palette.mode === 'dark' 
                    ? `${theme.palette.error.dark}20` 
                    : `${theme.palette.error.light}20`}
                sx={{ 
                    marginLeft: '16px',
                    borderColor: theme.palette.error.main,
                    '@media (max-width: 600px)': {
                        marginLeft: '8px',
                        padding: '8px 10px',
                        maxWidth: '95%',
                        borderRadius: '8px',
                    }
                }}
            >
                <Box className="flex items-start gap-2">
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.error.contrastText,
                            flexShrink: 0,
                            mt: 0.5,
                        }}
                    >
                        <ReportProblem sx={{ fontSize: 16 }} />
                    </Box>
                    
                    <Box className="flex-1">
                        <Typography 
                            fontWeight={600}
                            sx={{ 
                                color: theme.palette.error.main,
                                fontSize: '0.9rem'
                            }}
                        >
                            {isCredentialError ? '🔑 Authentication Error' : ' Error'}
                        </Typography>
                        <Typography 
                            variant="body2"
                            sx={{ 
                                color: theme.palette.text.secondary,
                                mt: 0.5,
                            }}
                        >
                            {isCredentialError 
                                ? 'The AI provider rejected the API credentials. Please reconfigure your API key.'
                                : error}
                        </Typography>
                        {isCredentialError && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => {
                                    const event = new CustomEvent('openModelConfig', {});
                                    window.dispatchEvent(event);
                                }}
                                sx={{
                                    mt: 1,
                                    textTransform: 'none',
                                    fontSize: '0.75rem'
                                }}
                            >
                                Reconfigure API Key
                            </Button>
                        )}
                    </Box>
                </Box>

                <Box className="flex justify-end mt-2">
                    <Typography 
                        fontSize={font.size.fs_6} 
                        color={theme.palette.text.disabled}
                        sx={{
                            '@media (max-width: 600px)': {
                                fontSize: '0.625rem',
                            }
                        }}
                    >
                        {dayjs(data?.createdAt || new Date()).format("DD MMM YYYY, hh:mm A")}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function FolderResponseComponent({ response }) {
    const { currentFolderData } = useApplicationContext();
    const theme = useTheme();

    return (
        <Box className="w-full flex flex-col gap-0 px-2 py-2"
            sx={{
                '@media (max-width: 600px)': {
                    padding: '4px 8px',
                }
            }}>
            <Box className="w-full flex flex-col gap-0" 
                sx={{ 
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                }}>
                <Typography fontSize={font.size.fs_5} fontWeight={"bold"} 
                    sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    Title: {response?.name}
                </Typography>
                <Typography fontSize={font.size.fs_5} className="" 
                    sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    Description: {response?.description}
                </Typography>
            </Box>
        </Box>
    )
}

function UserStoryResponseComponent({ response }) {
    const theme = useTheme();

    console.log("UserStoryResponseComponent received:", response);

    // If response is null or undefined
    if (!response) {
        return (
            <Typography variant="body2" color="text.secondary">
                No user story data available
            </Typography>
        );
    }

    return (
        <Box className="w-full flex flex-col gap-2 px-2 py-2"
            sx={{
                '@media (max-width: 600px)': {
                    padding: '4px 8px',
                }
            }}>
            <Box className="w-full flex flex-col gap-2" 
                sx={{ 
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                }}>
                {/* Title */}
                <Typography fontSize={font.size.fs_5} sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }} 
                    className="inline">
                    <Typography fontWeight={"bold"} color="inherit" component="span">Title: </Typography>
                    {response?.name || 'Untitled'}
                </Typography>
                
                {/* Description */}
                {response?.description && (
                    <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                            color: "inherit",
                            '@media (max-width: 600px)': {
                                fontSize: '0.875rem',
                            }
                        }}>
                        <Typography fontWeight={"bold"} color="inherit" component="span">Description: </Typography>
                        {response.description}
                    </Typography>
                )}
                
                {/* Story Points */}
                {response?.storyPointPlanned !== undefined && response?.storyPointPlanned !== null && (
                    <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                            color: "inherit",
                            '@media (max-width: 600px)': {
                                fontSize: '0.875rem',
                            }
                        }}>
                        <Typography fontWeight={"bold"} color="inherit" component="span">Story Points: </Typography>
                        {response.storyPointPlanned}
                    </Typography>
                )}
                
                {/* Acceptance Criteria */}
                {response?.acceptanceCriteria && response.acceptanceCriteria.length > 0 && (
                    <>
                        <Typography fontSize={font.size.fs_5} className="" fontWeight={"bold"} sx={{ 
                                color: "inherit",
                                '@media (max-width: 600px)': {
                                    fontSize: '0.875rem',
                                }
                            }}>
                            Acceptance Criteria:
                        </Typography>
                        <Box className="flex flex-col gap-1">
                            {response.acceptanceCriteria.map((step, index) => (
                                <Box key={index} className="flex items-start gap-2">
                                    <Box className="w-2 h-2 rounded-full mt-1.5" 
                                        sx={{ 
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? theme.palette.grey[400] 
                                                : theme.palette.grey[700],
                                            flexShrink: 0,
                                        }}>
                                    </Box>
                                    <Typography fontSize={font.size.fs_5} className="flex-1" sx={{ 
                                            color: "inherit",
                                            '@media (max-width: 600px)': {
                                                fontSize: '0.875rem',
                                            }
                                        }}>
                                        {step}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

function UseCaseResponseComponent({ response }) {
    const { currentFolderData } = useApplicationContext();
    const theme = useTheme();

    return (
        <Box className="w-full flex flex-col gap-0 px-2 py-2"
            sx={{
                '@media (max-width: 600px)': {
                    padding: '4px 8px',
                }
            }}>
            <Box className="w-full flex flex-col gap-2" 
                sx={{ 
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                }}>
                <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    <Typography fontWeight={"bold"} color="inherit" component="span">Title: </Typography>
                    {response?.name}
                </Typography>
                <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    <Typography fontWeight={"bold"} color="inherit" component="span">Description: </Typography>
                    {response?.description}
                </Typography>
                <Typography fontSize={font.size.fs_5} fontWeight={"bold"} className="" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    Steps:
                </Typography>
                {response?.useCaseSteps?.length > 0 && (
                    <Box className="flex flex-col gap-1">
                        {response?.useCaseSteps?.map((step, index) => (
                            <Box key={index} className="flex items-start gap-2">
                                <Box className="w-2 h-2 rounded-full mt-1.5" 
                                    sx={{ 
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? theme.palette.grey[400] 
                                            : theme.palette.grey[700],
                                        flexShrink: 0,
                                    }}>
                                </Box>
                                <Typography fontSize={font.size.fs_5} className="flex-1" sx={{ 
                                        color: "inherit",
                                        '@media (max-width: 600px)': {
                                            fontSize: '0.875rem',
                                        }
                                    }}>
                                    {step}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

function TestCaseResponseComponent({ response }) {
    const { currentFolderData } = useApplicationContext();
    const theme = useTheme();

    return (
        <Box className="w-full flex flex-col gap-0 px-2 py-2"
            sx={{
                '@media (max-width: 600px)': {
                    padding: '4px 8px',
                }
            }}>
            <Box className="w-full flex flex-col gap-2" 
                sx={{ 
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                }}>
                <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    <Typography fontWeight={"bold"} color="inherit" component="span">Title: </Typography>
                    {response?.name}
                </Typography>
                <Typography fontSize={font.size.fs_5} className="inline" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    <Typography fontWeight={"bold"} color="inherit" component="span">Description: </Typography>
                    {response?.description}
                </Typography>
                <Typography fontSize={font.size.fs_5} fontWeight={"bold"} className="" sx={{ 
                        color: "inherit",
                        '@media (max-width: 600px)': {
                            fontSize: '0.875rem',
                        }
                    }}>
                    Steps:
                </Typography>
                {response?.testCaseSteps?.length > 0 && (
                    <Box className="flex flex-col gap-1">
                        {response?.testCaseSteps?.map((step, index) => (
                            <Box key={index} className="flex items-start gap-2">
                                <Box className="w-2 h-2 rounded-full mt-1.5" 
                                    sx={{ 
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? theme.palette.grey[400] 
                                            : theme.palette.grey[700],
                                        flexShrink: 0,
                                    }}>
                                </Box>
                                <Box className="flex-1 flex flex-col" >
                                    <Typography fontSize={font.size.fs_5} className="flex-1" sx={{ 
                                            color: "inherit",
                                            '@media (max-width: 600px)': {
                                                fontSize: '0.875rem',
                                            }
                                        }}>
                                        Step: {step?.step}
                                    </Typography>
                                    <Typography fontSize={font.size.fs_5} className="flex-1" sx={{ 
                                            color: "inherit",
                                            '@media (max-width: 600px)': {
                                                fontSize: '0.875rem',
                                            }
                                        }}>
                                        Expected: {step?.expectedResult}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    )
}